import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Search, FileText, TrendingDown, CheckCircle2, ArrowRight, Phone, Clock, Shield, DollarSign, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_BG = "https://images.unsplash.com/photo-1559027877-de64d8a44d57?w=1920&q=80";
const IMG_STEP1 = "https://images.pexels.com/photos/9875683/pexels-photo-9875683.jpeg?auto=compress&cs=tinysrgb&w=600";
const IMG_STEP2 = "https://images.unsplash.com/photo-1766066014237-00645c74e9c6?w=600&q=80";
const IMG_STEP3 = "https://images.unsplash.com/photo-1758523419540-035814b85c80?w=600&q=80";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

const DETAILED_STEPS = [
  {
    num: "01",
    icon: Search,
    title: "Tell Us What You Need",
    desc: "Start by entering your address and selecting the energy service you want to compare.",
    details: [
      "Choose residential or business electricity",
      "Let us know if it's for home or business",
      "Share your address so we can find local deals",
      "Tell us about your current provider and usage",
    ],
    image: IMG_STEP1,
  },
  {
    num: "02",
    icon: FileText,
    title: "We Compare Providers",
    desc: "Our comparison specialists analyse the market to find the best electricity plan for your specific situation.",
    details: [
      "We analyse the market to find your best plan",
      "Our analysis considers your usage patterns",
      "We factor in discounts, fees and contract terms",
      "We identify genuine savings, not just headline rates",
    ],
    image: IMG_STEP2,
  },
  {
    num: "03",
    icon: TrendingDown,
    title: "You Save Money",
    desc: "We present you with the best options and handle the switching process for you. You enjoy the savings with zero hassle.",
    details: [
      "Choose the plan that suits you best",
      "We handle the entire switch process",
      "No interruption to your services",
      "Ongoing monitoring for future savings",
    ],
    image: IMG_STEP3,
  },
];

const SAVINGS_EXAMPLES = [
  { icon: Zap, title: "Electricity", before: "$1,800/yr", after: "$1,220/yr", savings: "$580/yr", desc: "Average household electricity savings" },
  { icon: Users, title: "Business Energy", before: "$12,000/yr", after: "$9,600/yr", savings: "$2,400/yr", desc: "Average small business energy savings" },
  { icon: DollarSign, title: "Business Electricity", before: "$12,000/yr", after: "$9,600/yr", savings: "$2,400/yr", desc: "Average small business electricity savings" },
];

export default function HowItWorksPage() {
  return (
    <div data-testid="how-it-works-page">
      {/* Hero with background */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="max-w-2xl" initial="hidden" animate="visible" variants={fadeUp}>
            <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">How It Works</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mt-2 mb-4">
              Saving Money Has Never Been Easier
            </h1>
            <p className="text-lg text-gray-300 leading-relaxed">
              Our streamlined comparison process makes it simple to find better energy deals. No obligation, no cost, no hassle.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Detailed Steps with images */}
      <section className="py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {DETAILED_STEPS.map((step, i) => (
            <motion.div
              key={i}
              className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            >
              <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#FF6600] flex items-center justify-center">
                    <step.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-xs font-semibold tracking-wider uppercase text-[#FF6600]">Step {step.num}</span>
                    <h2 className="text-2xl font-bold text-[#0B203B]">{step.title}</h2>
                  </div>
                </div>
                <p className="text-[#64748B] leading-relaxed mb-4">{step.desc}</p>
                <ul className="space-y-2">
                  {step.details.map((d, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#64748B]">
                      <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
              <div className={i % 2 === 1 ? "lg:order-1" : ""}>
                <img src={step.image} alt={step.title} className="rounded-2xl shadow-xl w-full h-[320px] object-cover" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 md:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2E2E33]">Why Our Process Works</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: "Under 2 Minutes", desc: "Our comparison form takes less than 2 minutes to complete." },
              { icon: Shield, title: "No Obligation", desc: "Compare freely - you're never pressured to switch." },
              { icon: DollarSign, title: "100% Free", desc: "Our service is completely free for you. Always." },
              { icon: CheckCircle2, title: "Expert Support", desc: "Our Australian team is here to help every step of the way." },
            ].map((b, i) => (
              <motion.div key={i} className="bg-white rounded-2xl border border-[#E2E8F0] p-6 text-center card-lift" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <div className="w-12 h-12 rounded-xl bg-[#0B203B] flex items-center justify-center mx-auto mb-4">
                  <b.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="font-bold text-[#0B203B] mb-2">{b.title}</h3>
                <p className="text-sm text-[#64748B]">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Examples */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-sm font-semibold tracking-wider uppercase text-[#FF6600]">Real Savings</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2E2E33] mt-2">See How Much You Could Save</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {SAVINGS_EXAMPLES.map((ex, i) => (
              <motion.div key={i} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 text-center card-lift" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i}>
                <ex.icon className="w-10 h-10 text-[#FF6600] mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="font-bold text-[#0B203B] text-lg mb-1">{ex.title}</h3>
                <p className="text-xs text-[#64748B] mb-4">{ex.desc}</p>
                <div className="flex items-center justify-center gap-4 mb-3">
                  <div>
                    <p className="text-lg line-through text-red-400">{ex.before}</p>
                    <p className="text-xs text-[#64748B]">Before</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#10B981]" />
                  <div>
                    <p className="text-lg font-bold text-[#10B981]">{ex.after}</p>
                    <p className="text-xs text-[#64748B]">After</p>
                  </div>
                </div>
                <div className="bg-[#10B981]/10 rounded-lg py-2 px-4">
                  <p className="text-lg font-extrabold text-[#10B981]">{ex.savings}</p>
                  <p className="text-xs text-[#10B981]">Annual Savings</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA with background */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
          <div className="absolute inset-0 bg-[#0B203B]/90" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Saving?</h2>
          <p className="text-gray-300 mb-8">It takes under 2 minutes to get your personalised comparison. No obligation, 100% free.</p>
          <Link to="/compare">
            <Button className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold rounded-full px-8 py-4 text-lg" data-testid="hiw-cta-compare">
              Get Your Free Quote <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
